// Export reference lists to BibTeX, RIS, or CSV. Style-aware CSV includes a
// formatted-citation column; file names include the chosen style.
import { formatReferencePlain, yearSuffixMap, applyYearSuffix } from "@/lib/citations";
import { normalizeAuthors } from "@/lib/authors";

function keyFor(ref, i) {
  const src = ref.authors_raw || ref.authors || "ref";
  const sur = src.split(/[;,]/)[0].trim().split(/\s+/).pop() || "ref";
  const yr = (ref.year || "").replace(/\D/g, "") || String(i + 1);
  return (sur.replace(/[^A-Za-z0-9]/g, "") || "ref") + yr;
}

function bibEscape(s) {
  return String(s || "").replace(/([&%$#_{}])/g, "\\$1");
}

function authorList(ref) {
  return (ref.authors_raw || ref.authors || "")
    .split(/\s*;\s*|\s+and\s+|\s*,?\s*&\s*/i)
    .map((a) => a.trim())
    .filter(Boolean);
}

export function toBibTeX(refs, style, styled, suffixes) {
  const sfx = suffixes || yearSuffixMap(refs);
  return refs
    .map((r, i) => {
      const t = (r.source_type || "").toLowerCase();
      const type = t === "book" ? "book" : t === "chapter" ? "incollection"
        : t === "report" ? "techreport" : t === "dissertation" ? "phdthesis"
        : t === "conference" ? "inproceedings" : (r.source ? "article" : "misc");
      const fields = [];
      const add = (k, v) => { if (v && String(v).trim()) fields.push(`  ${k} = {${bibEscape(String(v).trim())}}`); };
      if (styled) {
        const a = normalizeAuthors(r.authors_raw || r.authors || "", style);
        if (a) fields.push(`  author = {${bibEscape(a)}}`);
      } else {
        const authors = authorList(r);
        if (authors.length) fields.push(`  author = {${authors.map(bibEscape).join(" and ")}}`);
      }
      if (r.editors) add("editor", (r.editors || "").split(/\s*;\s*|\s+and\s+/i).map((x) => x.trim()).filter(Boolean).join(" and "));
      add("title", r.title);
      add("journal", r.source);
      add("booktitle", r.container);
      add("edition", r.edition);
      add("year", applyYearSuffix(r.year, sfx.get(r)));
      add("volume", r.volume);
      add("number", r.issue);
      add("pages", (r.pages || "").replace(/–/g, "--"));
      add("publisher", r.publisher);
      add("doi", r.doi);
      add("url", r.url);
      return `@${type}{${keyFor(r, i)},\n${fields.join(",\n")}\n}`;
    })
    .join("\n\n");
}

export function toRIS(refs, style, styled, suffixes) {
  const sfx = suffixes || yearSuffixMap(refs);
  return refs
    .map((r) => {
      const lines = [`TY  - ${r.source ? "JOUR" : "GEN"}`];
      if (styled) {
        const a = normalizeAuthors(r.authors_raw || r.authors || "", style);
        if (a) lines.push(`AU  - ${a}`);
      } else {
        authorList(r).forEach((a) => lines.push(`AU  - ${a}`));
      }
      if (r.year) lines.push(`PY  - ${applyYearSuffix(r.year, sfx.get(r))}`);
      if (r.title) lines.push(`TI  - ${r.title}`);
      if (r.source) lines.push(`JO  - ${r.source}`);
      if (r.volume) lines.push(`VL  - ${r.volume}`);
      if (r.issue) lines.push(`IS  - ${r.issue}`);
      const pg = (r.pages || "").split(/[–-]/);
      if (pg[0]) lines.push(`SP  - ${pg[0].trim()}`);
      if (pg[1]) lines.push(`EP  - ${pg[1].trim()}`);
      if (r.publisher) lines.push(`PB  - ${r.publisher}`);
      if (r.doi) lines.push(`DO  - ${r.doi}`);
      if (r.url) lines.push(`UR  - ${r.url}`);
      lines.push("ER  - ");
      return lines.join("\n");
    })
    .join("\n\n");
}

function csvCell(v) {
  const s = String(v == null ? "" : v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCSV(refs, style, styled, suffixes) {
  const sfx = suffixes || yearSuffixMap(refs);
  const headers = ["authors", "year", "title", "source", "container", "editors", "edition", "volume", "issue", "pages", "publisher", "doi", "url", `formatted_${style}`];
  const rows = refs.map((r) => {
    const authorsOut = styled ? normalizeAuthors(r.authors_raw || r.authors || "", style) : (r.authors_raw || r.authors || "");
    return [authorsOut, applyYearSuffix(r.year, sfx.get(r)), r.title, r.source, r.container, r.editors, r.edition, r.volume, r.issue, r.pages, r.publisher, r.doi, r.url, formatReferencePlain(r, style, sfx.get(r))]
      .map(csvCell)
      .join(",");
  });
  return [headers.join(","), ...rows].join("\n");
}

export function downloadText(filename, text, mime = "text/plain") {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportReferences(refs, style, format, styled = false, filenameBase = null) {
  const stamp = filenameBase || `references-${(style || "APA").toLowerCase()}`;
  const sfx = yearSuffixMap(refs);
  if (format === "bibtex") return downloadText(`${stamp}.bib`, toBibTeX(refs, style, styled, sfx), "application/x-bibtex");
  if (format === "ris") return downloadText(`${stamp}.ris`, toRIS(refs, style, styled, sfx), "application/x-research-info-systems");
  return downloadText(`${stamp}.csv`, toCSV(refs, style, styled, sfx), "text/csv");
}
